import { useCallback, useEffect, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useLocation } from 'react-router-dom'
import {
  getPagination,
  setSearchString,
  toggleToolFilter,
  toggleStatusFilter,
  clearAllFilters,
  clearToolFilters,
  clearStatusFilters,
  setPageNumber,
  setPageSize,
  setSorting,
  setTotalUsers,
  setUsersData,
  getUsersData,
  UserManagementFilterConstants,
} from '../../store/userManagement/userManagementSlice'
import {
  useGetClientAdminFiltersQuery,
  useGetClientAdminUsersQuery,
  FilterOptionsResponse,
} from '../../store/userManagement/userManagementSearchService'
import { RsmPaginationPageSizes } from '../../rsmCoreComponents/components/RsmPagination/RsmPagination'

/**
 * Custom React hook for User Management search functionality
 * Orchestrates search, filtering, pagination, and API calls
 * Follows the useInvoicesSearch pattern for consistency
 */
const useUserManagementSearch = () => {
  const location = useLocation()
  const dispatch = useDispatch()

  // Redux state selectors
  // Only subscribe to usersData - filtering/sorting/pagination handled in UserManagementLayout
  const pageInfo = useSelector(getPagination)
  const storedUsersData = useSelector(getUsersData)

  // Local state for search pending flags
  const [isSearchPending, setIsSearchPending] = useState<boolean>(false)
  const [isTextSearchPending, setIsTextSearchPending] = useState<boolean>(false)

  // RTK Query hooks for API calls
  const {
    isSuccess: filterIsSuccess,
    isError: filterIsError,
    data: filterData,
    error: filterError,
  } = useGetClientAdminFiltersQuery()

  // Call API once without parameters to get complete dataset
  // Skip subsequent calls if data already loaded (prevents refetch on pagination/filter changes)
  const {
    isLoading: usersIsLoading,
    isError: usersIsError,
    data: usersDataFromApi,
    error: usersError,
  } = useGetClientAdminUsersQuery(undefined, {
    skip: storedUsersData.length > 0,
  })

  // Store API response in Redux on successful fetch and update total count
  useEffect(() => {
    if (usersDataFromApi && !usersIsLoading && !usersIsError) {
      dispatch(setUsersData(usersDataFromApi))
      dispatch(setTotalUsers(usersDataFromApi.length))
    }
  }, [usersDataFromApi, usersIsLoading, usersIsError, dispatch])

  // Determine if search should run based on pending flags and filter success
  const shouldRunSearch = () =>
    (isSearchPending || isTextSearchPending) && filterIsSuccess

  // Execute search when conditions are met
  const submitSearch = useCallback(async () => {
    if (!shouldRunSearch()) return

    // Reset pending flags
    setIsSearchPending(false)
    setIsTextSearchPending(false)
  }, [isSearchPending, isTextSearchPending, filterIsSuccess])

  // Set search pending flags to trigger search
  const requestSearch = useCallback(
    (search: boolean, textSearch: boolean) => {
      setIsSearchPending(search)
      setIsTextSearchPending(textSearch)
    },
    [setIsSearchPending, setIsTextSearchPending],
  )

  // Pagination controls
  const setUserPageSize = useCallback(
    (pageSize: RsmPaginationPageSizes) => {
      dispatch(setPageSize(pageSize))
    },
    [dispatch],
  )

  const toPage = useCallback(
    (pageNumber: number) => {
      dispatch(setPageNumber(pageNumber))
    },
    [dispatch],
  )

  const nextPage = useCallback(() => {
    const currentPage = pageInfo.pageNumber
    dispatch(setPageNumber(currentPage + 1))
  }, [dispatch, pageInfo.pageNumber])

  const previousPage = useCallback(() => {
    const currentPage = pageInfo.pageNumber
    if (currentPage > 1) {
      dispatch(setPageNumber(currentPage - 1))
    }
  }, [dispatch, pageInfo.pageNumber])

  // Sorting functionality
  const SortUserData = useCallback(
    (sortBy: string, isAscending: 'asc' | 'desc') => {
      const sortDirection = isAscending === 'asc' ? 'asc' : 'desc'
      dispatch(setSorting({ sortBy, sortDirection }))
    },
    [dispatch],
  )

  // Filter management functions
  const changeFilterValue = useCallback(
    (filterType: string, value: string) => {
      if (filterType === UserManagementFilterConstants.tools) {
        dispatch(toggleToolFilter(value))
      } else if (filterType === UserManagementFilterConstants.status) {
        dispatch(toggleStatusFilter(value))
      }
    },
    [dispatch],
  )

  const clearFilters = useCallback(() => {
    dispatch(clearAllFilters())
  }, [dispatch])

  const clearCategoryFilter = useCallback(
    (categoryType: string) => {
      if (categoryType === UserManagementFilterConstants.tools) {
        dispatch(clearToolFilters())
      } else if (categoryType === UserManagementFilterConstants.status) {
        dispatch(clearStatusFilters())
      } else if (categoryType === UserManagementFilterConstants.search) {
        dispatch(setSearchString(''))
      }
    },
    [dispatch],
  )

  const changeFilterAndSearch = useCallback(
    (filterType: string, value: string) => {
      changeFilterValue(filterType, value)
    },
    [changeFilterValue],
  )

  const changeSearchFilterAndSearch = useCallback(
    (searchString: string) => {
      dispatch(setSearchString(searchString))
    },
    [dispatch],
  )

  // Effect to trigger search when location changes to User Management page
  useEffect(() => {
    if (
      location.pathname === '/user-management' ||
      location.pathname.includes('/user-management')
    ) {
      submitSearch()
    }
  }, [location.pathname, submitSearch])

  // Effect to run search when pending flags change
  useEffect(() => {
    submitSearch()
  }, [isSearchPending, isTextSearchPending, filterIsSuccess])

  // Return all exposed members as specified in the export schema
  return {
    // Filter API state
    filterIsSuccess,
    filterIsError,
    filterData: filterData as FilterOptionsResponse,
    filterError,

    // Users API state
    usersIsLoading,
    usersIsError,
    usersData: storedUsersData,
    usersError,

    // Pagination controls
    nextPage,
    previousPage,
    setUserPageSize,
    toPage,
    pageInfo,

    // Filter management
    clearFilters,
    clearCategoryFilter,
    changeFilterValue,

    // Search controls
    requestSearch,
    changeFilterAndSearch,
    changeSearchFilterAndSearch,

    // Sorting
    SortUserData,
  }
}

export default useUserManagementSearch
